import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon } from '@heroicons/react/24/outline';

interface EULAModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function EULAModal({ isOpen, onClose }: EULAModalProps) {
  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50"
            onClick={onClose}
          />
          
          {/* Modal */}
          <div className="flex min-h-full items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.3 }}
              className="relative w-full max-w-4xl bg-white rounded-lg shadow-2xl max-h-[90vh] overflow-hidden"
            >
              {/* Header with close button */}
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                <h1 className="text-2xl font-bold text-gray-900">
                  End User License Agreement (EULA)
                </h1>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <XMarkIcon className="h-6 w-6 text-gray-500" />
                </button>
              </div>

              {/* Content */}
              <div className="px-6 py-6 overflow-y-auto max-h-[calc(90vh-80px)]">
                <div className="prose prose-gray max-w-none">
                  <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6">
                    <p className="text-blue-800 text-sm">
                      This End User License Agreement ("Agreement" or "EULA") is a legal agreement between you ("Customer" or "End User") and Operisoft Technologies Private Limited ("Operisoft") regarding the use of the S3 Portal application ("Application").
                    </p>
                    <p className="text-blue-700 mt-2 text-sm font-medium">
                      By using this Application, you agree to the terms of this Agreement. If you do not agree, you may not use the Application.
                    </p>
                  </div>

                  <section className="mb-8">
                    <h2 className="text-xl font-semibold text-gray-900 mb-4">1. Grant of License</h2>
                    <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
                      <li>Operisoft grants the Customer a limited, non-exclusive, non-transferable, revocable license to use the Application solely within the Customer's AWS account, as deployed by Operisoft under the managed services agreement.</li>
                      <li>This license is granted only for operational use and does not constitute a sale or transfer of ownership of the Application.</li>
                    </ul>
                  </section>

                  <section className="mb-8">
                    <h2 className="text-xl font-semibold text-gray-900 mb-4">2. Restrictions</h2>
                    <p className="text-gray-700 mb-3">The Customer shall not:</p>
                    <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-1">
                      <li>Copy, modify, or create derivative works of the Application.</li>
                      <li>Sell, sublicense, distribute, or transfer the Application to any third party.</li>
                      <li>Reverse engineer, decompile, or disassemble the Application.</li>
                      <li>Use the Application outside the scope of Operisoft's managed services engagement.</li>
                    </ul>
                  </section>

                  <section className="mb-8">
                    <h2 className="text-xl font-semibold text-gray-900 mb-4">3. Ownership</h2>
                    <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
                      <li>The Application and all associated intellectual property rights remain the sole property of Operisoft.</li>
                      <li>No ownership rights are transferred to the Customer under this Agreement.</li>
                    </ul>
                  </section>

                  <section className="mb-8">
                    <h2 className="text-xl font-semibold text-gray-900 mb-4">4. Data & Security</h2>
                    <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
                      <li>All data accessed through the Application remains within the Customer's AWS account.</li>
                      <li>The Customer is fully responsible for:
                        <ul className="list-disc pl-6 mt-2 space-y-1">
                          <li>Data integrity, compliance, and backup.</li>
                          <li>AWS account-level security, IAM policies, and access management.</li>
                        </ul>
                      </li>
                      <li>Operisoft is not responsible for any data loss, security breach, or unauthorized access that may occur through use of the Application.</li>
                    </ul>
                  </section>

                  <section className="mb-8">
                    <h2 className="text-xl font-semibold text-gray-900 mb-4">5. Support & Updates</h2>
                    <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
                      <li>Operisoft may, at its discretion, provide updates or modifications to the Application.</li>
                      <li>Support for the Application is provided only as part of Operisoft's managed services agreement.</li>
                    </ul>
                  </section>

                  <section className="mb-8">
                    <h2 className="text-xl font-semibold text-gray-900 mb-4">6. Disclaimer of Warranties</h2>
                    <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
                      <li>The Application is provided "as is" without any warranties, express or implied.</li>
                      <li>Operisoft does not warrant that the Application will be error-free, secure, or uninterrupted.</li>
                    </ul>
                  </section>

                  <section className="mb-8">
                    <h2 className="text-xl font-semibold text-gray-900 mb-4">7. Limitation of Liability</h2>
                    <p className="text-gray-700 mb-3">Operisoft shall not be liable for any direct, indirect, incidental, or consequential damages, including but not limited to:</p>
                    <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-1">
                      <li>Data loss or corruption.</li>
                      <li>Business interruption.</li>
                      <li>Unauthorized access or breaches.</li>
                    </ul>
                    <p className="text-gray-700">The Customer assumes all risks associated with the use of the Application.</p>
                  </section>

                  <section className="mb-8">
                    <h2 className="text-xl font-semibold text-gray-900 mb-4">8. Termination</h2>
                    <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
                      <li>This Agreement is effective until terminated.</li>
                      <li>Operisoft may revoke the license at any time if the Customer breaches the terms of this Agreement or the managed services agreement.</li>
                      <li>Upon termination, the Customer must cease using the Application.</li>
                    </ul>
                  </section>

                  <section className="mb-8">
                    <h2 className="text-xl font-semibold text-gray-900 mb-4">9. Governing Law</h2>
                    <p className="text-gray-700 mb-4">
                      This Agreement shall be governed by and construed in accordance with the laws of India, without regard to conflict of law principles.
                    </p>
                  </section>

                  <section className="mb-8">
                    <h2 className="text-xl font-semibold text-gray-900 mb-4">10. Contact Information</h2>
                    <p className="text-gray-700 mb-3">For questions regarding this Agreement, please contact:</p>
                    <div className="bg-gray-100 p-4 rounded-lg">
                      <p className="text-gray-700">
                        <strong>Operisoft Technologies Private Limited</strong><br />
                        Office No. 301, Zion Complex Building, Plot No 273,<br />
                        Sector – 10, Kharghar, Navi Mumbai – 410210, MH, India<br />
                        Email: support@operisoft.com<br />
                        Phone: 07941056868
                      </p>
                    </div>
                  </section>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      )}
    </AnimatePresence>
  );
}